import { createSlice } from '@reduxjs/toolkit';

import {
  getOptimizerSettingsApi,
  updateOptimizerSettingsApi,
  getOptimizerOverviewApi,
  getOptimizerProcessStatusApi,
  getOptimizerMappingInfoApi,
  getOptimizerScopeCountApi,
  uploadOptimizerMappingApi,
  startOptimizerRunApi,
  stopOptimizerRunApi,
  downloadOptimizerRunReportCsvApi,
  lookupOptimizerSkuApi,
} from './listing-optimizer.api';

const initialState = {
  settings: null,
  getSettingsStatus: 'idle',
  updateSettingsStatus: 'idle',

  overview: null,
  getOverviewStatus: 'idle',

  mappingInfo: null,
  getMappingInfoStatus: 'idle',
  uploadMappingStatus: 'idle',

  scopeCount: null,
  scopeCountDetail: null,
  getScopeCountStatus: 'idle',

  processStatus: null,
  getProcessStatusStatus: 'idle',
  isOptimizerRunning: false,

  startRunStatus: 'idle',
  stopRunStatus: 'idle',
  downloadRunReportStatus: 'idle',

  lookupResult: null,
  lookupStatus: 'idle',

  error: null,
};

const listingOptimizerSlice = createSlice({
  name: 'listingOptimizer',
  initialState,
  reducers: {
    setProcessStatusFromSocket(state, action) {
      state.processStatus = action.payload;
      state.isOptimizerRunning = Boolean(action.payload?.isRunning);
    },
    clearLookupResult(state) {
      state.lookupResult = null;
      state.lookupStatus = 'idle';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getOptimizerSettingsApi.pending, (state) => {
        state.getSettingsStatus = 'loading';
      })
      .addCase(getOptimizerSettingsApi.fulfilled, (state, action) => {
        state.getSettingsStatus = 'succeeded';
        state.settings = action.payload;
      })
      .addCase(getOptimizerSettingsApi.rejected, (state, action) => {
        state.getSettingsStatus = 'failed';
        state.error = action.payload;
      })
      .addCase(updateOptimizerSettingsApi.pending, (state) => {
        state.updateSettingsStatus = 'loading';
      })
      .addCase(updateOptimizerSettingsApi.fulfilled, (state, action) => {
        state.updateSettingsStatus = 'succeeded';
        state.settings = {
          ...state.settings,
          ...action.payload,
          ...(action.payload?.itemSpecsConfig
            ? { itemSpecsConfig: action.payload.itemSpecsConfig }
            : {}),
        };
      })
      .addCase(updateOptimizerSettingsApi.rejected, (state) => {
        state.updateSettingsStatus = 'failed';
      })
      .addCase(getOptimizerOverviewApi.fulfilled, (state, action) => {
        state.getOverviewStatus = 'succeeded';
        state.overview = action.payload;
      })
      .addCase(getOptimizerMappingInfoApi.fulfilled, (state, action) => {
        state.getMappingInfoStatus = 'succeeded';
        state.mappingInfo = action.payload;
      })
      .addCase(getOptimizerScopeCountApi.pending, (state) => {
        state.getScopeCountStatus = 'loading';
      })
      .addCase(getOptimizerScopeCountApi.fulfilled, (state, action) => {
        state.getScopeCountStatus = 'succeeded';
        state.scopeCount = action.payload?.count ?? 0;
        state.scopeCountDetail = action.payload ?? null;
      })
      .addCase(getOptimizerScopeCountApi.rejected, (state) => {
        state.getScopeCountStatus = 'failed';
        state.scopeCount = null;
        state.scopeCountDetail = null;
      })
      .addCase(uploadOptimizerMappingApi.fulfilled, (state, action) => {
        state.uploadMappingStatus = 'succeeded';
        state.mappingInfo = {
          exists: true,
          entryCount: action.payload?.entryCount ?? 0,
        };
      })
      .addCase(getOptimizerProcessStatusApi.fulfilled, (state, action) => {
        state.getProcessStatusStatus = 'succeeded';
        state.processStatus = action.payload;
        state.isOptimizerRunning = Boolean(action.payload?.isRunning);
      })
      .addCase(startOptimizerRunApi.fulfilled, (state, action) => {
        state.startRunStatus = 'succeeded';
        if (action.payload?.processStatus) {
          state.processStatus = action.payload.processStatus;
          state.isOptimizerRunning = Boolean(action.payload.processStatus.isRunning);
        } else {
          state.isOptimizerRunning = true;
        }
      })
      .addCase(startOptimizerRunApi.rejected, (state) => {
        state.startRunStatus = 'failed';
        state.isOptimizerRunning = false;
      })
      .addCase(stopOptimizerRunApi.fulfilled, (state) => {
        state.stopRunStatus = 'succeeded';
      })
      .addCase(downloadOptimizerRunReportCsvApi.pending, (state) => {
        state.downloadRunReportStatus = 'loading';
      })
      .addCase(downloadOptimizerRunReportCsvApi.fulfilled, (state) => {
        state.downloadRunReportStatus = 'succeeded';
      })
      .addCase(downloadOptimizerRunReportCsvApi.rejected, (state) => {
        state.downloadRunReportStatus = 'failed';
      })
      .addCase(lookupOptimizerSkuApi.pending, (state) => {
        state.lookupStatus = 'loading';
      })
      .addCase(lookupOptimizerSkuApi.fulfilled, (state, action) => {
        state.lookupStatus = 'succeeded';
        state.lookupResult = action.payload;
      })
      .addCase(lookupOptimizerSkuApi.rejected, (state) => {
        state.lookupStatus = 'failed';
        state.lookupResult = null;
      });
  },
});

export const { setProcessStatusFromSocket, clearLookupResult } = listingOptimizerSlice.actions;
export default listingOptimizerSlice.reducer;
